import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useState } from 'react';
import { ColorTheme } from '@figma/fpl-themes';
import clsx from 'clsx';
import { ButtonPrimitive } from '../button-primitive/button-primitive.js';
import { Arrow } from '../popover-primitive/popover-primitive.arrow.js';
import { Container as Container$1 } from '../popover-primitive/popover-primitive.container.js';
import { usePopover } from '../popover-primitive/popover-primitive.hooks.js';
import { iconTriggerButton, icon, container, bottomArrow, iconLead, text, content, hasIconLead, shortcutText, trail } from './toggle-tip.css.js';

const ARROW_HEIGHT = 6;
const ARROW_WIDTH = 12;
const ARROW_INSET = 1;
/**
 * Hook to manage the state and props for a toggletip component.
 *
 * @param options - Options for the toggletip. Common options include `placement`, `offset`, and `padding`.
 * @returns ToggleTipManager
 */
function useUncontrolledToggleTip(options) {
    const [isOpen, setOpen] = useState(false);
    const { getArrowProps, getContainerProps, getTriggerProps } = usePopover({
        ...options,
        isOpen,
        onOpenChange: setOpen,
        type: 'tooltip',
        arrowPadding: 5, // Border radius of the popover var(--radius-medium) is 5px
        openOnHover: true,
    });
    return {
        isOpen,
        toggle: () => setOpen((prev) => !prev),
        getTriggerProps: () => {
            return {
                ...getTriggerProps(),
                'data-fpl-toggle-tip-override': options.allowOnDisabled ?? undefined,
            };
        },
        getArrowProps,
        getContainerProps,
    };
}
/**
 * Icon trigger component for the toggletip. Consumers can use their own button for a trigger,
 * or this component for a default icon button style.
 *
 * @compose { "standalone": true }
 */
const IconTrigger = forwardRef(({ children, ...props }, ref) => (jsx(ButtonPrimitive, { "data-fpl-component": "", ...props, ref: ref, className: iconTriggerButton, children: jsx("span", { "aria-hidden": true, className: icon, children: children }) })));
IconTrigger.displayName = 'ToggleTip.IconTrigger';
/**
 * Container component for the toggletip, which renders the content and arrow.
 * Handles the positioning and outer styling of the toggletip.
 *
 * @compose { "root": true, "strictChildren": true }
 */
const Container = forwardRef(({ manager, children, ...props }, ref) => {
    // This translate is applied to hide box shadow between arrow and container
    // We can get away with always translating up by 1 because of how the translation is ordered with the rotation transform
    // if we change versions of floating ui, this may change.
    const translate = { x: 0, y: -1 };
    return (jsx(ColorTheme, { mode: "dark", children: jsxs(Container$1, { "data-fpl-component": true, ...props, ref: ref, ...manager.getContainerProps(), className: container, children: [jsx(Arrow, { ...manager.getArrowProps(), width: ARROW_WIDTH + 2 * ARROW_INSET, height: ARROW_HEIGHT + ARROW_INSET, className: bottomArrow, translate: translate }), jsx(Arrow, { width: ARROW_WIDTH + 2 * ARROW_INSET, height: ARROW_HEIGHT + ARROW_INSET, ...manager.getArrowProps(), translate: translate }), children] }) }));
});
Container.displayName = 'ToggleTip.Container';
/**
 * Content component for the toggletip, adds padding. Should always be a child of `Container`.
 *
 * @compose { "parent": ["ToggleTip.Container"] }
 */
const Content = forwardRef(({ children, iconLead: iconLead$1, maxWidth = '180px', ...props }, ref) => (jsx(ColorTheme, { mode: "dark", children: jsxs("div", { "data-fpl-component": true, ...props, ref: ref, className: clsx(content, { [hasIconLead]: !!iconLead$1 }), style: { maxWidth }, children: [iconLead$1 && jsx("span", { className: iconLead, children: iconLead$1 }), jsx("span", { className: text, children: children })] }) })));
Content.displayName = 'ToggleTip.Content';
/**
 * Shortcut component for the toggletip content. Must be a child of `ToggleTip.Trail`.
 *
 * @compose { "parent": ["ToggleTip.Trail"] }
 */
const Shortcut = forwardRef((props, ref) => (jsx(ColorTheme, { mode: "dark", children: jsx("span", { "data-fpl-component": true, ...props, ref: ref, className: shortcutText }) })));
Shortcut.displayName = 'ToggleTip.Shortcut';
/**
 * If a toggletip has button(s), or keyboard shortcuts, wrap them in ToggleTip.Trail
 *
 * @compose { "parent": ["ToggleTip.Container"] }
 */
const Trail = forwardRef((props, ref) => (jsx(ColorTheme, { mode: "dark", children: jsx("div", { "data-fpl-component": true, ...props, ref: ref, className: trail }) })));
Trail.displayName = 'ToggleTip.Trail';

export { Container, Content, IconTrigger, Shortcut, Trail, useUncontrolledToggleTip };
//# sourceMappingURL=toggle-tip.js.map
